import { createFileRoute } from "@tanstack/react-router";
import { ROLE_LABELS, roleLabel } from "@/lib/role-labels";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, Shield, ShieldCheck, ShieldAlert, ArrowUp, ArrowDown, ArrowRight, Camera, X, Pencil, Check, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { listManagers, removeManager, updateManagerRole, sendPasswordResetForUser } from "@/lib/users.functions";
import { inviteManager, resendActivationLink } from "@/lib/invites.functions";
import { updateUserName, updateUserEmail } from "@/lib/profile.functions";
import { supabase } from "@/integrations/supabase/client";
import { UserAvatar } from "@/components/UserAvatar";

export const Route = createFileRoute("/admin/brugere")({
  component: AdminUsers,
});

function AdminUsers() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ id: string; email: string } | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<{ id: string; email: string } | null>(null);
  const [demoteTarget, setDemoteTarget] = useState<{ id: string; email: string } | null>(null);
  const [resetPasswordTarget, setResetPasswordTarget] = useState<{ id: string; email: string } | null>(null);
  const [promoteConfirmText, setPromoteConfirmText] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [removeAvatarTarget, setRemoveAvatarTarget] = useState<{ id: string; label: string } | null>(null);
  const [editTarget, setEditTarget] = useState<{ id: string; fullName: string; email: string; createdAt: string; lastSignInAt: string | null } | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [confirmEmailChange, setConfirmEmailChange] = useState<{ oldEmail: string; newEmail: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const targetUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? null));
  }, []);

  const triggerAvatarUpload = (userId: string) => {
    targetUserIdRef.current = userId;
    fileInputRef.current?.click();
  };

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const userId = targetUserIdRef.current;
    e.target.value = "";
    if (!file || !userId) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Filen skal være et billede");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Billedet må højst være 5MB");
      return;
    }

    setUploadingFor(userId);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${userId}/avatar-${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        // Path includes Date.now(); 1y immutable cache is safe.
        .upload(path, file, { upsert: true, cacheControl: "31536000" });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);

      const { error: upsertError } = await supabase
        .from("profiles")
        .upsert(
          { user_id: userId, avatar_url: urlData.publicUrl },
          { onConflict: "user_id" }
        );
      if (upsertError) throw upsertError;

      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      if (userId === currentUserId) {
        await queryClient.invalidateQueries({ queryKey: ["my-profile", userId] });
      }
      toast.success("Profilbillede opdateret");
    } catch (err) {
      toast.error("Kunne ikke uploade billede", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setUploadingFor(null);
      targetUserIdRef.current = null;
    }
  };

  const handleAvatarRemove = async (userId: string) => {
    setUploadingFor(userId);
    try {
      const { data: files } = await supabase.storage.from("avatars").list(userId);
      if (files && files.length > 0) {
        await supabase.storage
          .from("avatars")
          .remove(files.map((f) => `${userId}/${f.name}`));
      }
      const { error } = await supabase
        .from("profiles")
        .upsert(
          { user_id: userId, avatar_url: null },
          { onConflict: "user_id" }
        );
      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      if (userId === currentUserId) {
        await queryClient.invalidateQueries({ queryKey: ["my-profile", userId] });
      }
      toast.success("Profilbillede fjernet");
    } catch (err) {
      toast.error("Kunne ikke fjerne billede", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setUploadingFor(null);
    }
  };

  const listManagersFn = useServerFn(listManagers);
  const inviteManagerFn = useServerFn(inviteManager);
  const removeManagerFn = useServerFn(removeManager);
  const updateRoleFn = useServerFn(updateManagerRole);
  const updateUserNameFn = useServerFn(updateUserName);
  const updateUserEmailFn = useServerFn(updateUserEmail);
  const resendActivationFn = useServerFn(resendActivationLink);
  const sendPasswordResetFn = useServerFn(sendPasswordResetForUser);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Not authenticated");
      return listManagersFn({ headers: { Authorization: `Bearer ${token}` } });
    },
  });

  const inviteMutation = useMutation({
    mutationFn: async ({ email, fullName }: { email: string; fullName: string }) => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Not authenticated");
      return inviteManagerFn({
        data: { email, fullName },
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setDialogOpen(false);
      setEmail("");
      setFullName("");
      toast.success("Manager inviteret", { description: "En email er sendt med login-oplysninger" });
    },
    onError: (error: Error) => {
      toast.error("Fejl", { description: error.message });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Not authenticated");
      return removeManagerFn({
        data: { userId },
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Bruger fjernet");
    },
    onError: (error: Error) => {
      toast.error("Fejl", { description: error.message });
    },
  });

  const saveEditMutation = useMutation({
    mutationFn: async ({
      userId,
      fullName,
      email,
      originalFullName,
      originalEmail,
    }: {
      userId: string;
      fullName: string;
      email: string;
      originalFullName: string;
      originalEmail: string;
    }) => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Not authenticated");
      const headers = { Authorization: `Bearer ${token}` };

      const trimmedName = fullName.trim();
      const trimmedEmail = email.trim().toLowerCase();

      if (trimmedName !== originalFullName.trim()) {
        await updateUserNameFn({ data: { userId, fullName: trimmedName }, headers });
      }
      if (trimmedEmail && trimmedEmail !== originalEmail.trim().toLowerCase()) {
        await updateUserEmailFn({ data: { userId, email: trimmedEmail }, headers });
      }
      return { userId };
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      if (variables.userId === currentUserId) {
        await queryClient.invalidateQueries({ queryKey: ["my-profile", variables.userId] });
      }
      setEditTarget(null);
      toast.success("Bruger opdateret");
    },
    onError: (error: Error) => {
      toast.error("Kunne ikke opdatere bruger", { description: error.message });
    },
  });

  const resendActivationMutation = useMutation({
    mutationFn: async (userId: string) => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Not authenticated");
      return resendActivationFn({
        data: { userId },
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: (result) => {
      toast.success("Aktiveringslink sendt", {
        description: `En email er sendt til ${result.email} med link til at sætte adgangskode.`,
      });
    },
    onError: (error: Error) => {
      toast.error("Kunne ikke sende aktiveringslink", { description: error.message });
    },
  });

  const sendPasswordResetMutation = useMutation({
    mutationFn: async (userId: string) => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Not authenticated");
      return sendPasswordResetFn({
        data: {
          userId,
          redirectTo: `${window.location.origin}/reset-password`,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: (result) => {
      toast.success("Password-reset-mail sendt", {
        description: `${result.email} kan nu klikke på linket i mailen og sætte en ny adgangskode.`,
      });
    },
    onError: (error: Error) => {
      toast.error("Kunne ikke sende password-reset", { description: error.message });
    },
  });

  const openEditDialog = (user: { id: string; fullName: string; email: string; createdAt: string; lastSignInAt: string | null }) => {
    setEditTarget({
      id: user.id,
      fullName: user.fullName,
      email: user.email,
      createdAt: user.createdAt,
      lastSignInAt: user.lastSignInAt,
    });
    setEditName(user.fullName);
    setEditEmail(user.email);
  };

  const promoteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Not authenticated");
      return updateRoleFn({
        data: { userId, role: "admin" as const },
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Bruger opgraderet til Admin");
    },
    onError: (error: Error) => {
      toast.error("Fejl", { description: error.message });
    },
  });

  const demoteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Not authenticated");
      return updateRoleFn({
        data: { userId, role: "moderator" as const },
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Bruger nedgraderet til Manager");
    },
    onError: (error: Error) => {
      toast.error("Fejl", { description: error.message });
    },
  });

  const users = data?.users || [];
  const adminCount = users.filter((u) => u.role === "admin" || u.role === "super_admin").length;
  const currentUser = users.find((u) => u.id === currentUserId);
  const isCurrentSuperAdmin = currentUser?.role === "super_admin";
  const isCurrentAdmin = currentUser?.role === "admin" || isCurrentSuperAdmin;

  const roleLabels = ROLE_LABELS;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Brugere</h1>
          <p className="mt-1 text-sm text-muted-foreground">Inviter og administrer managers</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-accent text-accent-foreground hover:opacity-90"
        >
          <UserPlus size={16} className="mr-2" /> Inviter manager
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleAvatarFileChange}
      />

      <div className="mt-8 overflow-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Navn</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Rolle</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Sidste login</th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Indlæser...
                </td>
              </tr>
            ) : users.length > 0 ? (
              users.map((user) => (
                <tr key={user.id} className="border-t border-border hover:bg-muted/50">
                  <td className="px-4 py-3 font-medium text-foreground">
                    <div className="flex items-center gap-3">
                      <div className="relative shrink-0">
                        <button
                          type="button"
                          onClick={() => triggerAvatarUpload(user.id)}
                          disabled={uploadingFor === user.id}
                          className="group relative block rounded-full transition hover:opacity-80 disabled:opacity-50"
                          title="Skift profilbillede"
                          aria-label={`Skift profilbillede for ${user.fullName || user.email}`}
                        >
                          <UserAvatar
                            id={user.id}
                            name={user.fullName}
                            email={user.email}
                            avatarUrl={user.avatarUrl}
                          />
                          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition group-hover:opacity-100">
                            {uploadingFor === user.id ? (
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            ) : (
                              <Camera size={14} className="text-white" />
                            )}
                          </div>
                        </button>
                        {user.avatarUrl && (
                          <button
                            type="button"
                            onClick={() => setRemoveAvatarTarget({ id: user.id, label: user.fullName || user.email })}
                            disabled={uploadingFor === user.id}
                            className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-md transition hover:scale-110 disabled:opacity-50"
                            title="Fjern profilbillede"
                            aria-label="Fjern profilbillede"
                          >
                            <X size={10} strokeWidth={3} />
                          </button>
                        )}
                      </div>
                      <span>{user.fullName || "—"}</span>
                      {user.id === currentUserId && (
                        <span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent">
                          Dig
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <span>{user.email}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-sm">
                      {user.role === "super_admin" ? (
                        <ShieldAlert size={14} className="text-accent" />
                      ) : user.role === "admin" ? (
                        <ShieldCheck size={14} className="text-accent" />
                      ) : (
                        <Shield size={14} className="text-blue-500" />
                      )}
                      {roleLabel(user.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {user.lastSignInAt
                      ? new Date(user.lastSignInAt).toLocaleString("da-DK", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "Aldrig"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditDialog(user)}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-accent hover:text-accent"
                        title="Rediger bruger"
                      >
                        <Pencil size={12} /> Rediger
                      </button>
                      {isCurrentSuperAdmin && user.id !== currentUserId && (
                        <button
                          onClick={() => setResetPasswordTarget({ id: user.id, email: user.email })}
                          disabled={
                            sendPasswordResetMutation.isPending &&
                            sendPasswordResetMutation.variables === user.id
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-accent hover:text-accent disabled:opacity-50"
                          title="Send password-reset-mail til denne bruger"
                        >
                          <KeyRound size={12} />
                          {sendPasswordResetMutation.isPending &&
                          sendPasswordResetMutation.variables === user.id
                            ? "Sender..."
                            : "Reset password"}
                        </button>
                      )}
                      {/* Super Admin: vis ingen ændre/fjern-knapper (man kan ikke nedgradere super_admin via UI; gør via DB) */}
                      {user.role === "super_admin" ? null : user.role === "admin" ? (
                        // Admin: kun super_admin må nedgradere/fjerne
                        isCurrentSuperAdmin && adminCount > 1 && (
                          <>
                            <button
                              onClick={() => setDemoteTarget({ id: user.id, email: user.email })}
                              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-destructive hover:text-destructive"
                              title="Nedgrader til Manager"
                            >
                              <ArrowDown size={12} /> Nedgrader
                            </button>
                            <button
                              onClick={() => setRemoveTarget({ id: user.id, email: user.email })}
                              className="text-muted-foreground hover:text-destructive"
                              title="Fjern admin"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )
                      ) : user.role === "moderator" ? (
                        <>
                          {/* Promovering af Manager → Admin er tilladt for både admin og super_admin */}
                          {isCurrentAdmin && (
                            <button
                              onClick={() => setPromoteTarget({ id: user.id, email: user.email })}
                              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-accent hover:text-accent"
                              title="Opgrader til Admin"
                            >
                              <ArrowUp size={12} /> Opgrader
                            </button>
                          )}
                          <button
                            onClick={() => setRemoveTarget({ id: user.id, email: user.email })}
                            className="text-muted-foreground hover:text-destructive"
                            title="Fjern manager"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      ) : (
                        // Almindelige brugere kan kun fjernes — ikke opgraderes direkte til Admin
                        <button
                          onClick={() => setRemoveTarget({ id: user.id, email: user.email })}
                          className="text-muted-foreground hover:text-destructive"
                          title="Fjern bruger"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Ingen brugere endnu
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Inviter ny manager</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              inviteMutation.mutate({ email, fullName });
            }}
            className="space-y-4"
          >
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Navn</label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Fulde navn"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@eksempel.dk"
                required
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Manageren får adgang til at administrere biler og feed-indstillinger. De modtager en email med login-oplysninger.
            </p>
            <Button
              type="submit"
              disabled={inviteMutation.isPending}
              className="w-full bg-accent text-accent-foreground hover:opacity-90"
            >
              {inviteMutation.isPending ? "Inviterer..." : "Send invitation"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fjern manager?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil fjerne <strong>{removeTarget?.email}</strong> som manager?
              De mister adgang til admin-området med det samme.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (removeTarget) {
                  removeMutation.mutate(removeTarget.id);
                  setRemoveTarget(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Fjern
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!promoteTarget}
        onOpenChange={(open) => {
          if (!open) {
            setPromoteTarget(null);
            setPromoteConfirmText("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert size={18} className="text-accent" />
              Bekræft rolle-ændring
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Du er ved at opgradere <strong className="text-foreground">{promoteTarget?.email}</strong>.
                  Læs ændringen igennem før du bekræfter.
                </p>

                {/* Visual role transition */}
                <div className="flex items-center justify-center gap-3 rounded-lg border border-border bg-muted/40 p-4">
                  <div className="flex flex-col items-center gap-1">
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-sm font-medium text-muted-foreground">
                      <Shield size={14} /> Manager
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Nuværende</span>
                  </div>
                  <ArrowRight size={18} className="text-accent" aria-hidden />
                  <div className="flex flex-col items-center gap-1">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-accent px-2.5 py-1 text-sm font-semibold text-accent-foreground">
                      <ShieldCheck size={14} /> Admin
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-accent">Ny rolle</span>
                  </div>
                </div>

                {/* What this grants */}
                <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-foreground">
                  <p className="font-medium">Som Admin kan brugeren:</p>
                  <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-muted-foreground">
                    <li>Oprette og fjerne andre Managers</li>
                    <li>Administrere alle biler, leads og indhold</li>
                    <li>Ændre indstillinger (undtagen super_admin-handlinger)</li>
                  </ul>
                </div>

                {/* Type-to-confirm */}
                <div className="space-y-1.5">
                  <label htmlFor="promote-confirm" className="text-sm font-medium text-foreground">
                    Skriv <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">OPGRADER</code> for at bekræfte
                  </label>
                  <Input
                    id="promote-confirm"
                    value={promoteConfirmText}
                    onChange={(e) => setPromoteConfirmText(e.target.value)}
                    placeholder="OPGRADER"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuller</AlertDialogCancel>
            <AlertDialogAction
              disabled={promoteConfirmText.trim() !== "OPGRADER"}
              onClick={() => {
                if (promoteTarget && promoteConfirmText.trim() === "OPGRADER") {
                  promoteMutation.mutate(promoteTarget.id);
                  setPromoteTarget(null);
                  setPromoteConfirmText("");
                }
              }}
              className="bg-accent text-accent-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Opgrader til Admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!demoteTarget} onOpenChange={(open) => !open && setDemoteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert size={18} className="text-destructive" />
              Bekræft rolle-ændring
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Du er ved at nedgradere <strong className="text-foreground">{demoteTarget?.email}</strong>.
                  De mister adgang til brugeradministration og indstillinger.
                </p>

                <div className="flex items-center justify-center gap-3 rounded-lg border border-border bg-muted/40 p-4">
                  <div className="flex flex-col items-center gap-1">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-accent px-2.5 py-1 text-sm font-semibold text-accent-foreground">
                      <ShieldCheck size={14} /> Admin
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Nuværende</span>
                  </div>
                  <ArrowRight size={18} className="text-destructive" aria-hidden />
                  <div className="flex flex-col items-center gap-1">
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-sm font-medium text-muted-foreground">
                      <Shield size={14} /> Manager
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-destructive">Ny rolle</span>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (demoteTarget) {
                  demoteMutation.mutate(demoteTarget.id);
                  setDemoteTarget(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Nedgrader til Manager
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!resetPasswordTarget}
        onOpenChange={(open) => !open && setResetPasswordTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send password-reset?</AlertDialogTitle>
            <AlertDialogDescription>
              Der bliver sendt en mail til <strong>{resetPasswordTarget?.email}</strong> med et
              link til at sætte en ny adgangskode. Brugerens nuværende adgangskode forbliver
              gyldig indtil de selv vælger en ny via linket. Linket udløber typisk efter 1 time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (resetPasswordTarget) {
                  sendPasswordResetMutation.mutate(resetPasswordTarget.id);
                  setResetPasswordTarget(null);
                }
              }}
              className="bg-accent text-accent-foreground hover:opacity-90"
            >
              Send reset-mail
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removeAvatarTarget} onOpenChange={(open) => !open && setRemoveAvatarTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fjern profilbillede?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil fjerne profilbilledet for <strong>{removeAvatarTarget?.label}</strong>?
              Avataren falder tilbage til de farvede initialer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (removeAvatarTarget) {
                  handleAvatarRemove(removeAvatarTarget.id);
                  setRemoveAvatarTarget(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Fjern billede
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmEmailChange} onOpenChange={(open) => !open && setConfirmEmailChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bekræft email-ændring</AlertDialogTitle>
            <AlertDialogDescription>
              Du er ved at ændre email fra <strong>{confirmEmailChange?.oldEmail}</strong> til{" "}
              <strong>{confirmEmailChange?.newEmail}</strong>.
              <br />
              <br />
              Brugeren skal logge ind med den nye email næste gang. Hvis adressen er forkert, kan
              brugeren blive låst ude af sin konto. Er du sikker?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (editTarget) {
                  saveEditMutation.mutate({
                    userId: editTarget.id,
                    fullName: editName,
                    email: editEmail,
                    originalFullName: editTarget.fullName,
                    originalEmail: editTarget.email,
                  });
                }
                setConfirmEmailChange(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ja, ændre email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rediger bruger</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const trimmedNewEmail = editEmail.trim().toLowerCase();
                const trimmedOldEmail = editTarget.email.trim().toLowerCase();
                if (trimmedNewEmail && trimmedNewEmail !== trimmedOldEmail) {
                  setConfirmEmailChange({ oldEmail: editTarget.email, newEmail: editEmail.trim() });
                  return;
                }
                saveEditMutation.mutate({
                  userId: editTarget.id,
                  fullName: editName,
                  email: editEmail,
                  originalFullName: editTarget.fullName,
                  originalEmail: editTarget.email,
                });
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-3 rounded-md border border-border bg-muted/40 p-3 text-xs">
                <div>
                  <div className="font-medium text-muted-foreground">Oprettet</div>
                  <div className="mt-0.5 text-foreground">
                    {editTarget.createdAt
                      ? new Date(editTarget.createdAt).toLocaleString("da-DK", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </div>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">Sidste login</div>
                  <div className="mt-0.5 text-foreground">
                    {editTarget.lastSignInAt
                      ? new Date(editTarget.lastSignInAt).toLocaleString("da-DK", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "Aldrig"}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <span className="mt-0.5" aria-hidden>⚠️</span>
                <p>
                  <strong>Bemærk:</strong> Ændringer kan ikke fortrydes. Brugeren skal logge ind med den nye email næste gang.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Navn</label>
                <Input
                  autoFocus
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={100}
                  placeholder="Fulde navn"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Email</label>
                <Input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  maxLength={255}
                  placeholder="email@eksempel.dk"
                  required
                />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => editTarget && resendActivationMutation.mutate(editTarget.id)}
                  disabled={resendActivationMutation.isPending || saveEditMutation.isPending}
                  title="Send et nyt aktiverings-/nulstillingslink til brugerens email"
                >
                  <Mail size={14} className="mr-1.5" />
                  {resendActivationMutation.isPending ? "Sender..." : "Send aktiveringslink"}
                </Button>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditTarget(null)}
                    disabled={saveEditMutation.isPending}
                  >
                    Annuller
                  </Button>
                  <Button
                    type="submit"
                    disabled={saveEditMutation.isPending}
                    className="bg-accent text-accent-foreground hover:opacity-90"
                  >
                    {saveEditMutation.isPending ? "Gemmer..." : "Gem ændringer"}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
